import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, getLocale } from 'umi';
import { LANGUAGE_EN } from '@intelligence/_config/config';
import StoreRankTopCore from '@/pages/intelligence/pages/StoreRanks/components/StoreRankTopCore';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import WechatGamesTopFilter from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/WechatGamesTopFilter';
import useMiniGameRankings from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/hooks/useMiniGameRankings';
import { FILTER_EXTEND_NAMESPACE } from '@/pages/intelligence/models/filterExtend';
import * as FILTER_KEYS from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useMetricsDateRange from '@intelligence/v3/_hooks/useMetricsDateRange';
import { FilterTimeType } from '@intelligence/utils/filterUtils';
import './index.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import { handleRangeMiniGameMoreFilterExtend, changeMiniGameRankingsFormat } from '@intelligence/utils/dateUtils';
import useEarliesTimeHasData from '@/pages/intelligence/hooks/useEarliesTimeHasData';
import { hasDataTimerParams } from '@intelligence/utils/timerDefautParams';
import {
  MINI_GAME_RANKING_KEY_MINIGAME_LIST,
  MINI_GAME_GENRE_SOURCE,
  MINI_GAME_RANKING_KEY_LIST,
  MINI_GAME_ENTITY_TYPE,
  MINI_GAME_DETAIL_PAGE_PATH,
} from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';
import moment from 'moment';

const GAME_ID_KEY = 'game_id';

const MORE_PAGE_PATH = '/intelligence/casualGamesHub/casualMiniGameRankings/more/wechatgames';

// 默认筛选器数据，用于reset时、兜底
const DEFAULT_FILTER: any = {
  entity_type: MINI_GAME_ENTITY_TYPE,
  markets: ['global'],
  genre_source: MINI_GAME_GENRE_SOURCE,
  granularity: 'daily',
  start_date: '',
  end_date: '',
};

type IMobileStoreRankTopProps = IBaseProps & {
  condKey?: string;
};

const WechatGamesTop: React.FC<IMobileStoreRankTopProps> = (props) => {
  const { condKey } = props;

  const { latestTimeTypeHasData } = useEarliesTimeHasData({
    filterType: hasDataTimerParams?.storerankMobileTopcharts?.type,
    rangeTimeType: FilterTimeType.sameDay,
  });
  const locale = getLocale();
  const isEN = locale === LANGUAGE_EN;
  const { userInfo } = useSelector((state: any) => {
    const { userInfo } = state.home;
    return {
      userInfo,
    };
  });
  const { filterExtend, setFilterExtend } = useFilterExtend({});
  const filterState = useSelector(
    (rootState: any) => rootState?.[FILTER_EXTEND_NAMESPACE],
  );
  // 是否操作过筛选器，判断是否继承的条件
  const isOperate = filterState?.isOperate || false;
  const [initialFilter, setinitialFilter] = useState(DEFAULT_FILTER);

  const { loading: metricsGranularityDateRangesLoading, granularityDateRangeMap: metricsGranularityDateRangeMap } =
    useMetricsDateRange(true, {
      game_ids: [],
      app_ids: [],
      granularities: ['daily'],
      markets: [],
      metrics: MINI_GAME_RANKING_KEY_MINIGAME_LIST,
      group_by: ['date'],
    });

  const {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化
    onPageInit,
    // 筛选器confirm的回调
    onFilterConfirm,
    // 筛选器reset的回调
    onFilterReset,
    // 榜单卡片点击游戏的回调（跳转游戏详情页）
    onCardGameClick,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore,
    // 获取游戏详情页路由path
    getDetailGamePath,
  } = useMiniGameRankings({
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath: MORE_PAGE_PATH,
    // 榜单卡片点击游戏 跳转游戏详情页的 路由path
    detailPagePath: MINI_GAME_DETAIL_PAGE_PATH,
    // 页面打点配置的key
    pageLogCfgKey: 'mobileStoreRankTop',
    // 是否操作过筛选器
    isOperate,
    // 初始化筛选器数据
    initialFilter,
    metricTypeList: MINI_GAME_RANKING_KEY_LIST,
  });

  useEffect(() => {
    if (!userInfo?.userid) {
      // 拿缓存值需要拿到用户id
      return;
    }
    if (metricsGranularityDateRangesLoading) return;

    const initFilter = async () => {
      let newFetchParams = { ...DEFAULT_FILTER };
      let shouldSetFilterExtend = true;
      // console.debug('filterState', filterState)
      // 优先级：1. 用户手动选择的参数（filterExtend） 2. 后端默认值（condKey） 3. 保存的筛选方案
      if (filterState.isOperate === true) {
        /** 优先获取用户手动选择的继承参数 */
        const extendParams = getExtendParams();
        // console.debug('extendParamsextendParams', extendParams)
        if (extendParams && Object.keys(extendParams).length > 0) {
          newFetchParams = { ...newFetchParams, ...extendParams };
          // console.debug('newFetchParamsnewFetchParams', newFetchParams)
          shouldSetFilterExtend = true;
        }
      }
      const dateObj = metricsGranularityDateRangeMap[''] || ({} as any);

      if (!newFetchParams.start_date || !newFetchParams.end_date) {
        const today = new Date().toISOString().split('T')[0];
        // console.debug('newFetchParamsnewFetchParams', newFetchParams)
        newFetchParams.start_date = dateObj?.min_date || today;
        newFetchParams.end_date = dateObj?.min_date || today;
      }
      // 设置继承值，使下钻页能够继承这些筛选参数（传递处理后的完整参数，包含 filter、start_date、end_date）
      if (shouldSetFilterExtend) {
        setFilterExtend(newFetchParams);
      }
      setinitialFilter(newFetchParams);
      onPageInit(newFetchParams);
    };
    initFilter();
  }, [userInfo?.userid, condKey, metricsGranularityDateRangesLoading, metricsGranularityDateRangeMap]);

  const getExtendParams = () => {
    return handleFilterParams(filterExtend);
  };

  const handleFilterParams = (payload: any) => {
    // console.debug('handleFilterParamspayload', payload)
    const params = handleRangeMiniGameMoreFilterExtend(payload);
    // console.debug('params234234', params)
    const newFetchParams: any = {};
    const timeExtend = params?.[FILTER_KEYS?.RANGE_TIME];
    const genreExtend = params?.[FILTER_KEYS?.GENRETOR];

    if (timeExtend) {
      newFetchParams.start_date = timeExtend?.start_date;
      newFetchParams.end_date = timeExtend?.end_date;
    }
    if (genreExtend) {
      newFetchParams.minigame_genre = genreExtend?.minigame_genre || [];
    }

    return {
      ...newFetchParams,
    };
  };

  const topsCur = useMemo(() => {
    const defaultDate: any = metricsGranularityDateRangeMap[''] || {};
    const maxDate = defaultDate?.max_date || moment().format('YYYY-MM-DD');
    const topsTmp = changeMiniGameRankingsFormat(tops, maxDate, isEN, MINI_GAME_RANKING_KEY_LIST);
    return topsTmp;
  }, [tops, isEN, metricsGranularityDateRangeMap]);

  return (
    <StoreRankTopCore
      className="dbi-pc-store-rank-top dbi-alltopcharts dbi-mini-game-rank-top"
      entityType={MINI_GAME_ENTITY_TYPE}
      // 游戏id的取值字段
      gameIdKey={GAME_ID_KEY}
      isLoading={isLoading}
      tops={topsCur}
      filter={
        <WechatGamesTopFilter
          style={{ marginBottom: !isLoading && topsCur?.length > 0 ? 10 : 20 }}
          isOperate={isOperate}
          morePagePath={MORE_PAGE_PATH}
          morePageSource={topsCur?.[0]?.name}
          filter={filter}
          onConfirm={onFilterConfirm}
          onReset={onFilterReset}
          rangeTimeComponentDefault={latestTimeTypeHasData}
          entityType={MINI_GAME_ENTITY_TYPE}
        />
      }
      // 点击游戏跳转详情页
      onCardGameClick={onCardGameClick}
      getDetailGamePath={getDetailGamePath}
      onCardTitleClick={onGoToStoreRankMore}
      isShowMarketIcon={false}
    />
  );
};

export default WechatGamesTop;
